/**
 * 결제 수단 선택 섹션
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 *
 * 저장된 카드 목록을 표시하고 선택/관리할 수 있는 컴포넌트입니다.
 */
import { useLanguage } from '@/contexts/AppContext';
import { useCallback } from 'react';
import { ChevronUp, ChevronDown, Plus, Star, Trash2, Edit2 } from 'lucide-react';

const PAYMENT_METHOD_ARIA = {
  ko: {
    setAsDefault: (name: string) => `${name}을(를) 기본 카드로 설정`,
    editNickname: (name: string) => `${name} 별명 수정`,
    deleteCard: (name: string) => `${name} 삭제`,
  },
  es: {
    setAsDefault: (name: string) => `Establecer ${name} como tarjeta predeterminada`,
    editNickname: (name: string) => `Editar apodo de ${name}`,
    deleteCard: (name: string) => `Eliminar ${name}`,
  },
  en: {
    setAsDefault: (name: string) => `Set ${name} as default card`,
    editNickname: (name: string) => `Edit nickname for ${name}`,
    deleteCard: (name: string) => `Delete ${name}`,
  },
} as const;

import type { SavedCard, CardBrand } from '@/lib/api/types';

/**
 * 카드 브랜드별 스타일 설정
 */
const CARD_BRAND_STYLES: Record<CardBrand, { bg: string; text: string; label: string }> = {
  VISA: {
    bg: 'bg-blue-600',
    text: 'text-white',
    label: 'VISA',
  },
  MASTERCARD: {
    bg: 'bg-red-500',
    text: 'text-white',
    label: 'MC',
  },
  AMEX: {
    bg: 'bg-green-600',
    text: 'text-white',
    label: 'AMEX',
  },
  UNKNOWN: {
    bg: 'bg-gray-500',
    text: 'text-white',
    label: 'CARD',
  },
};

export interface PaymentMethodsSectionProps {
  /** 선택된 카드 ID */
  selectedCardId: number | null;
  /** 저장된 카드 목록 */
  savedCards: SavedCard[];
  /** 섹션 확장 여부 */
  isExpanded: boolean;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 섹션 확장/축소 토글 */
  onToggleExpanded: () => void;
  /** 카드 선택 */
  onCardSelect: (cardId: number) => void;
  /** 새 카드 추가 */
  onAddNewCard: () => void;
  /** '새 카드 추가' 버튼 노출 여부 — 카드 저장은 회원 전용(auth:customer)이라 게스트에겐 false. 기본 true(하위 호환). */
  showAddCard?: boolean;
  /** 카드 삭제 */
  onDeleteCard?: (cardId: number) => void;
  /** 카드 편집 (별명 수정) */
  onEditCard?: (card: SavedCard) => void;
  /** 기본 카드 설정 */
  onSetDefault?: (cardId: number) => void;
  /** 번역 텍스트 */
  t: {
    paymentMethod: string;
    selectCard: string;
    registerNewCard: string;
    defaultCard: string;
    expired: string;
    loading?: string;
    noCards?: string;
  };
}

export function PaymentMethodsSection({
  selectedCardId,
  savedCards,
  isExpanded,
  isLoading = false,
  onToggleExpanded,
  onCardSelect,
  onAddNewCard,
  showAddCard = true,
  onDeleteCard,
  onEditCard,
  onSetDefault,
  t,
}: PaymentMethodsSectionProps) {
  const { language } = useLanguage();
  const aria = PAYMENT_METHOD_ARIA[language] ?? PAYMENT_METHOD_ARIA.es;
  const selectedCard = savedCards.find((c) => c.id === selectedCardId);

  /**
   * 카드 브랜드 배지 렌더링
   * useCallback으로 메모이제이션하여 불필요한 재생성 방지
   */
  const renderCardBrandBadge = useCallback((brand: CardBrand) => {
    const style = CARD_BRAND_STYLES[brand] || CARD_BRAND_STYLES.UNKNOWN;
    return (
      <div
        className={`w-8 h-5 rounded flex items-center justify-center flex-shrink-0 ${style.bg}`}
      >
        <span className={`text-[10px] font-bold ${style.text}`}>
          {style.label}
        </span>
      </div>
    );
  }, []);

  /**
   * 선택된 카드 미리보기 (축소 상태)
   */
  const renderSelectedPreview = () => {
    if (isLoading) {
      return (
        <span className="text-gray-400 text-sm animate-pulse">
          {t.loading || '로딩 중...'}
        </span>
      );
    }

    if (!selectedCard) {
      return (
        <span className="text-gray-400 text-sm">
          {t.selectCard}
        </span>
      );
    }

    return (
      <>
        {renderCardBrandBadge(selectedCard.card_brand)}
        <span className="font-mono text-sm flex-shrink-0">
          •••• {selectedCard.card_last_four}
        </span>
        {selectedCard.is_default && (
          <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0" />
        )}
      </>
    );
  };

  return (
    <div className="sm:p-4 border-b border-border px-4 py-3">
      {/* 헤더 - 확장/축소 토글 */}
      <button
        onClick={onToggleExpanded}
        className="w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
        type="button"
        aria-expanded={isExpanded}
        aria-controls="saved-cards-list"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-700">
              {t.paymentMethod}
            </span>
            {renderSelectedPreview()}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* 확장된 카드 목록 */}
      {isExpanded && (
        <div
          id="saved-cards-list"
          className="mt-4 space-y-3 max-h-80 overflow-y-auto"
          role="listbox"
          aria-label={t.selectCard}
        >
          {/* 로딩 상태 */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}

          {/* 카드 없음 상태 */}
          {!isLoading && savedCards.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              {t.noCards || '등록된 카드가 없습니다'}
            </div>
          )}

          {/* 카드 목록 */}
          {!isLoading &&
            savedCards.map((card) => (
              <div
                key={card.id}
                role="option"
                aria-selected={selectedCardId === card.id}
                className={`w-full p-3 rounded-lg flex items-center justify-between transition-colors ${
                  selectedCardId === card.id
                    ? 'bg-primary/10 border-2 border-primary'
                    : card.is_usable
                    ? 'bg-muted border-2 border-transparent hover:bg-muted/80'
                    : 'bg-muted border-2 border-transparent opacity-60'
                }`}
              >
                {/* 카드 선택 영역 */}
                <button
                  onClick={() => card.is_usable && onCardSelect(card.id)}
                  className="flex items-center gap-3 min-w-0 flex-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                  disabled={!card.is_usable}
                  type="button"
                  aria-label={
                    card.is_usable
                      ? `${card.display_name} 선택`
                      : `${card.display_name} (${card.is_expired ? t.expired : '사용 불가'})`
                  }
                >
                  {renderCardBrandBadge(card.card_brand)}
                  <div className="text-left min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">
                        {card.display_name}
                      </p>
                      {card.is_default && (
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                      )}
                      {card.is_expired && (
                        <span className="text-xs text-red-500 font-medium">
                          {t.expired}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {card.expiry_display}
                      {card.card_holder && ` · ${card.card_holder}`}
                    </p>
                  </div>
                </button>

                {/* 카드 마지막 4자리 */}
                <span className="font-mono text-sm flex-shrink-0 mr-2">
                  •••• {card.card_last_four}
                </span>

                {/* 카드 액션 버튼 */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* 기본 카드 설정 */}
                  {onSetDefault && !card.is_default && card.is_usable && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefault(card.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-amber-500 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                      title={t.defaultCard}
                      type="button"
                      aria-label={aria.setAsDefault(card.display_name)}
                    >
                      <Star className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}

                  {/* 별명 편집 */}
                  {onEditCard && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCard(card);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Edit"
                      type="button"
                      aria-label={aria.editNickname(card.display_name)}
                    >
                      <Edit2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}

                  {/* 삭제 */}
                  {onDeleteCard && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCard(card.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      title="Delete"
                      type="button"
                      aria-label={aria.deleteCard(card.display_name)}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}

          {/* 새 카드 추가 버튼 — 카드 저장은 회원 전용이라 게스트(showAddCard=false)에겐 미노출 */}
          {showAddCard && (
            <button
              onClick={onAddNewCard}
              className="w-full p-3 border-2 border-dashed border-border rounded-lg flex items-center justify-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              type="button"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">{t.registerNewCard}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default PaymentMethodsSection;
